import { useState } from "react";
import { AppHeader } from "@/components/AppHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Bell, Mail, Lock, Trash2, Info } from "lucide-react";

const Settings = () => {
  const [settings, setSettings] = useState({
    emailNotifications: true,
    notificationEmail: "docteur@exemple.fr",
    retentionDays: 90
  });
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  
  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };
  
  const handleSaveNotifications = () => {
    toast.success("Paramètres de notification mis à jour");
  };
  
  const handleChangePassword = (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    
    if (passwordData.newPassword.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères");
      return;
    }
    
    toast.success("Mot de passe modifié avec succès");
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: ""
    });
  };
  
  return (
    <div className="flex min-h-screen flex-col bg-muted/20">
      <AppHeader />
      
      <main className="flex-1">
        <div className="container-custom py-8">
          <div className="mb-8">
            <h1 className="mb-2 text-3xl font-bold">Paramètres</h1>
            <p className="text-muted-foreground">
              Gérez les paramètres de votre compte et vos préférences
            </p>
          </div>
          
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Notifications */}
            <Card className="border-2">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-primary" />
                  <CardTitle>Notifications</CardTitle>
                </div>
                <CardDescription>
                  Configurez vos préférences de notification
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="email-notifications">Notifications par email</Label>
                    <p className="text-sm text-muted-foreground">
                      Recevez un email pour chaque nouveau message
                    </p>
                  </div>
                  <Switch
                    id="email-notifications"
                    checked={settings.emailNotifications}
                    onCheckedChange={(checked) => handleSettingChange('emailNotifications', checked)}
                  />
                </div>
                
                {settings.emailNotifications && (
                  <div className="space-y-2">
                    <Label htmlFor="notification-email">Email de notification</Label>
                    <div className="flex gap-2">
                      <Input
                        id="notification-email"
                        type="email"
                        value={settings.notificationEmail}
                        onChange={(e) => handleSettingChange('notificationEmail', e.target.value)}
                        placeholder="votre@email.fr"
                      />
                    </div>
                  </div>
                )}
                
                <Button onClick={handleSaveNotifications} className="w-full">
                  Enregistrer les préférences
                </Button>
              </CardContent>
            </Card>
            
            {/* Password */}
            <Card className="border-2">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Lock className="h-5 w-5 text-primary" />
                  <CardTitle>Mot de passe</CardTitle>
                </div>
                <CardDescription>
                  Modifiez votre mot de passe de connexion
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="current-password">Mot de passe actuel</Label>
                    <Input
                      id="current-password"
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="new-password">Nouveau mot de passe</Label>
                    <Input
                      id="new-password"
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirmer le mot de passe</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      required
                    />
                  </div>
                  
                  <Button type="submit" className="w-full">
                    Changer le mot de passe
                  </Button>
                </form>
              </CardContent>
            </Card>
            
            {/* Data Retention */}
            <Card className="border-2">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Info className="h-5 w-5 text-primary" />
                  <CardTitle>Conservation des messages</CardTitle>
                </div>
                <CardDescription>
                  Informations sur la conservation de vos données
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-lg bg-muted/50 p-4">
                  <div className="mb-2 flex items-center gap-2">
                    <span className="text-2xl font-bold text-primary">{settings.retentionDays}</span>
                    <span className="text-sm text-muted-foreground">jours</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Les messages vocaux et leurs transcriptions sont conservés pendant {settings.retentionDays} jours par défaut.
                  </p>
                </div>
                
                <p className="text-sm text-muted-foreground">
                  Vous pouvez archiver ou supprimer manuellement vos messages à tout moment depuis votre boîte de réception.
                </p>
                
                <div className="rounded-lg border border-warning/20 bg-warning/5 p-4">
                  <p className="text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Note :</span> La durée de conservation est définie pour respecter les recommandations légales en matière de données de santé.
                  </p>
                </div>
              </CardContent>
            </Card>
            
            {/* Danger Zone */}
            <Card className="border-2 border-destructive/20">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Trash2 className="h-5 w-5 text-destructive" />
                  <CardTitle className="text-destructive">Zone dangereuse</CardTitle>
                </div>
                <CardDescription>
                  Actions irréversibles sur votre compte
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4">
                  <h3 className="mb-2 font-semibold text-foreground">Supprimer tous les messages</h3>
                  <p className="mb-4 text-sm text-muted-foreground">
                    Supprime définitivement tous vos messages et transcriptions. Cette action est irréversible.
                  </p>
                  <Button 
                    variant="destructive" 
                    onClick={() => toast.error("Fonctionnalité désactivée en mode démo")}
                  >
                    Supprimer tous les messages
                  </Button>
                </div>
                
                <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4">
                  <h3 className="mb-2 font-semibold text-foreground">Supprimer mon compte</h3>
                  <p className="mb-4 text-sm text-muted-foreground">
                    Supprime définitivement votre compte et toutes vos données. Cette action est irréversible.
                  </p>
                  <Button 
                    variant="destructive"
                    onClick={() => toast.error("Fonctionnalité désactivée en mode démo")}
                  >
                    Supprimer mon compte
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Settings;